import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
	IconButton,
	LinearProgress,
	MenuItem,
	Typography,
} from "@mui/material";
import { useState } from "react";
import * as yup from "yup";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { ICancelProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { ProposalFilesUploadArea } from "../components/ProposalFilesUploadArea/ProposalFileUploadArea";
import { cancelReasons } from "../helpers/reasonCancel";
import { useCancelProposal } from "./hooks/useCancelProposal";
import { format, parseISO } from "date-fns";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
	isOpenModal: boolean;
	isClose: () => void;
	rows: any;
}

export const ModalCancelProposal: React.FC<Props> = ({ isOpenModal, isClose, rows }) => {
	const { formRef } = useVForm();
	const [file, setFile] = useState<File>();
	const [reasonCancel, setReasonCancel] = useState("");
	const [isUploading, setIsUploading] = useState(false);
	const [clearFile, setClearFile] = useState(false);

	const rawTermBegin = rows?.termBegin?.substring(0, 10) || "";
	const termBeginDate = rawTermBegin ? parseISO(rawTermBegin) : new Date();
	const termBeginLabel = rawTermBegin ? format(termBeginDate, "dd/MM/yyyy") : "data inválida";

	// Definindo limites
	const today = new Date();
	const cancelWindowDays = rows?.renovatedId ? 120 : 60;
	const maxBackdate = new Date(today);
	maxBackdate.setDate(today.getDate() - cancelWindowDays);

	const todayStr = today.toISOString().substring(0, 10);
	const maxBackdateStr = maxBackdate.toISOString().substring(0, 10);
	const termBeginStr = termBeginDate.toISOString().substring(0, 10);

	const schema = yup.object().shape({
		canceledType: yup.string().required("Campo obrigatório"),
		canceledReason: yup.string().required("Campo obrigatório"),
		canceledAt: yup
			.string()
			.transform((value, originalValue) => (originalValue === "" ? null : originalValue))
			.required("Campo obrigatório")
			.nullable()
			.test("is-valid-date", "Data inválida", (value) => {
				if (!value) return false;
				const parsedDate = new Date(value.substring(0, 10));
				return !isNaN(parsedDate.getTime());
			})
			.test("valid-range", (value, context) => {
				if (!value) return true;

				const dateStr = value.substring(0, 10);

				if (dateStr < termBeginStr) {
					return context.createError({
						message: `Data não pode ser anterior ao início da garantia (${termBeginLabel})`,
					});
				}

				if (dateStr < maxBackdateStr) {
					return context.createError({
						message: `Data não pode ser menor que ${cancelWindowDays} dias (${format(maxBackdate, "dd/MM/yyyy")})`,
					});
				}

				if (dateStr > todayStr) {
					return context.createError({
						message: `Data não pode ser maior que hoje (${format(today, "dd/MM/yyyy")})`,
					});
				}

				return true;
			}),
	});

	const { submit, isSubmitting } = useCancelProposal({
		proposalId: rows.id,
		createdAt: rows.created_at,
	});

	const handleSubmit = async (dados: ICancelProposal) => {
		try {
			await schema.validate(dados, { abortEarly: false });

			if (!file) {
				formRef.current?.setErrors({
					canceledReason: "É necessário anexar o termo de rescisão para prosseguir.",
				});
				return;
			}

			await submit(dados, file);
		} catch (err: any) {
			if (err?.name === "ValidationError") {
				const validationErrors: IVFormErros = {};
				err.inner.forEach((error: any) => {
					if (!error.path) return;
					validationErrors[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErrors);
			}
		}
	};

	const handleBlur = async (field: keyof ICancelProposal, value: any) => {
		try {
			await schema.fields[field].validate(value);
			formRef.current?.setFieldError(field, "");
		} catch (error: any) {
			formRef.current?.setFieldError(field, error.message);
		}
	};

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography variant="h5" sx={{ fontWeight: 300, color: "primary.main" }}>
						Cancelar Garantia
					</Typography>
					<IconButton onClick={isClose} size="small" color="inherit">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<VForm ref={formRef} onSubmit={handleSubmit} onPointerEnterCapture={""} onPointerLeaveCapture={""} placeholder="">
				<DialogContent dividers>
					{isSubmitting && <LinearProgress />}

					<Alert severity="warning" sx={{ mb: 2 }}>
						Para efetivar o cancelamento, é necessário anexar o termo de rescisão contratual conforme o motivo.
					</Alert>

					<Grid container spacing={2}>
						<Grid item xs={12} md={8}>
							<VTextField
								fullWidth
								label="Motivo do cancelamento"
								name="canceledType"
								select
								onChange={(e) => setReasonCancel(e.target.value)}
								onBlur={(e) => handleBlur("canceledType", e.target.value)}
							>
								{cancelReasons.map((reason) => (
									<MenuItem key={reason.value} value={reason.value}>
										{reason.label}
									</MenuItem>
								))}
							</VTextField>
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField
								fullWidth
								label="Data do cancelamento"
								name="canceledAt"
								type="date"
								onBlur={(e) => handleBlur("canceledAt", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<VTextField
								fullWidth
								label="Descreva o motivo"
								name="canceledReason"
								multiline
								rows={4}
								onBlur={(e) => handleBlur("canceledReason", e.target.value)}
							/>
						</Grid>
					</Grid>

					<ProposalFilesUploadArea onFileUploadHandler={setFile} isLoadingProp={isUploading} clear={clearFile} />
				</DialogContent>

				<DialogActions>
					<Button onClick={isClose} color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>}>
						Voltar
					</Button>
					<Button type="submit" color="primary" variant="contained" startIcon={<Icon>check</Icon>} disabled={!reasonCancel || isSubmitting}>
						Cancelar Garantia
					</Button>
				</DialogActions>
			</VForm>
		</Dialog>
	);
};
